<template>
  <div class="page-content">
    <div class="page-header">
      <div>
        <BreadcrumbComponent :items="items" />
        <h1 class="page-title">Liste des Lots</h1>
        <p class="page-subtitle" v-if="results.length > 0">{{ results.length }} lot{{ results.length > 1 ? 's' : '' }}</p>
      </div>
      <div class="d-flex" style="gap: 8px;">
        <v-btn color="error" outlined small @click="exportPDF" v-if="results.length > 0">
          <v-icon left small>mdi-file-pdf-box</v-icon> PDF
        </v-btn>
        <v-btn color="success" outlined small @click="exportExcel" v-if="results.length > 0">
          <v-icon left small>mdi-file-excel</v-icon> Excel
        </v-btn>
        <v-btn color="primary" depressed :to="'/new-batche'">
          <v-icon left small>mdi-plus</v-icon> Nouveau lot
        </v-btn>
      </div>
    </div>

    <PreloaderComponent :enable="enable" />

    <div v-if="enable">
      <!-- Search -->
      <div class="d-flex justify-end mb-3">
        <input v-model="search" type="text" placeholder="Rechercher..." class="search-input" />
      </div>

      <v-card flat class="table-card" v-if="filteredResults.length > 0">
        <v-simple-table dense>
          <thead>
            <tr>
              <th class="text-left">#</th>
              <th class="text-left">MÉMO</th>
              <th class="text-center">TYPE</th>
              <th class="text-center">PRODUITS</th>
              <th class="text-center">QUANTITÉ</th>
              <th class="text-center">DATE</th>
              <th class="text-center">HEURE</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="batch in filteredResults" :key="batch.id" @click="$router.push('/batches/' + batch.id)" class="clickable-row">
              <td class="font-weight-bold">#{{ batch.no }}</td>
              <td>{{ batch.memo || '—' }}</td>
              <td class="text-center">
                <v-chip x-small :color="getBatchTypeColor(batch)" dark>{{ getBatchTypeLabel(batch) }}</v-chip>
              </td>
              <td class="text-center">
                <v-chip x-small outlined color="primary">{{ getProductsCount(batch) }}</v-chip>
              </td>
              <td class="text-center font-weight-medium">{{ getTotalQuantity(batch) }}</td>
              <td class="text-center text-caption">{{ batch.created_at | formatDate }}</td>
              <td class="text-center text-caption">{{ batch.created_at | formatTime }}</td>
              <td style="width:40px">
                <v-btn icon x-small>
                  <v-icon small color="primary">mdi-chevron-right</v-icon>
                </v-btn>
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>

      <v-card flat v-else class="empty-card">
        <v-card-text class="text-center py-12">
          <v-icon size="56" color="grey lighten-2">mdi-package-variant</v-icon>
          <h3 class="text-body-1 font-weight-medium mt-4 mb-1">Aucun lot trouvé</h3>
          <p class="text-caption text--secondary">Modifiez les filtres de date ou créez un nouveau lot.</p>
        </v-card-text>
      </v-card>
    </div>
  </div>
</template>

<script>
import PreloaderComponent from '../components/PreloaderComponent.vue'
import BreadcrumbComponent from '../components/BreadcrumbComponent.vue'

export default {
  name: 'BatchReportView',
  components: { BreadcrumbComponent, PreloaderComponent },
  data: () => ({
    search: '',
    results: [],
    enable: false,
    items: [
      { text: 'Inventaire', disabled: false, href: '/batches' },
      { text: 'Liste des Lots', disabled: true, href: '#' }
    ],
  }),

  computed: {
    filteredResults() {
      if (!this.search.trim()) return this.results
      var q = this.search.toLowerCase()
      return this.results.filter(function (b) {
        return (b.no && String(b.no).toLowerCase().indexOf(q) !== -1) ||
               (b.memo && b.memo.toLowerCase().indexOf(q) !== -1)
      })
    }
  },

  methods: {
    // L'API /batches renvoie `type` (-1 si aucune opération), `operations_count` et
    // `total_quantity` — pas de tableau `operations`.
    getBatchType(batch) {
      return batch.type == null || batch.type == -1 ? null : batch.type
    },
    getBatchTypeLabel(batch) {
      var t = this.getBatchType(batch)
      if (t == null) return '—'
      return t == 1 ? 'Réception' : (t == 2 ? 'Ajustement' : 'Autre')
    },
    getBatchTypeColor(batch) {
      var t = this.getBatchType(batch)
      if (t == null) return 'grey'
      return t == 1 ? 'success' : (t == 2 ? 'info' : 'warning')
    },
    getProductsCount(batch) {
      return batch.operations_count != null ? batch.operations_count : '—'
    },
    getTotalQuantity(batch) {
      return batch.total_quantity != null ? this.$options.filters.formatNumber(batch.total_quantity) : '—'
    },

    load() {
      this.enable = false
      var self = this
      var from = localStorage.debut_periode || ''
      var to = localStorage.fin_periode || ''
      this.$axios.get('/batches?limit=100&from=' + from + '&to=' + to)
        .then(function (r) { self.results = r.data.result || []; self.enable = true })
        .catch(function () { self.enable = true })
    },

    fmt(v) {
      if (!v && v !== 0) return '0.00'
      var p = Math.abs(parseFloat(v)).toFixed(2).split('.')
      p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      return p.join('.')
    },

    async exportPDF() {
      var self = this
      var { jsPDF } = await import('jspdf')
      var doc = new jsPDF('l', 'mm', 'a4')
      var pw = 297, ph = 210, ml = 12, mr = pw - 12, cw = mr - ml
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var primary = [25, 65, 115], dark = [30, 41, 59], muted = [100, 116, 139], border = [220, 225, 230]

      doc.setFillColor(...primary); doc.rect(0, 0, pw, 18, 'F')
      doc.setFontSize(12); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
      doc.text('LISTE DES LOTS', ml, 11)
      doc.setFontSize(8); doc.setFont('helvetica', 'normal'); doc.setTextColor(200, 215, 235)
      if (biz.name) doc.text(biz.name, mr, 11, { align: 'right' })

      var y = 24
      var cols = [
        { label: '#', w: 20, align: 'left' },
        { label: 'MÉMO', w: 70, align: 'left' },
        { label: 'TYPE', w: 25, align: 'center' },
        { label: 'PRODUITS', w: 20, align: 'center' },
        { label: 'QUANTITÉ', w: 22, align: 'center' },
        { label: 'DATE', w: 24, align: 'center' },
        { label: 'HEURE', w: 18, align: 'center' },
      ]
      var totalW = cols.reduce(function (s, c) { return s + c.w }, 0)
      var scale = cw / totalW; cols.forEach(function (c) { c.w = Math.round(c.w * scale) })
      var actualW = cols.reduce(function (s, c) { return s + c.w }, 0); cols[1].w += (cw - actualW)
      var cx = ml; cols.forEach(function (c) { c.x = cx; cx += c.w })
      var rowH = 6.5

      function drawHeader() {
        doc.setFillColor(...primary); doc.rect(ml, y, cw, rowH + 1, 'F')
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(255, 255, 255)
        cols.forEach(function (c) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(c.label, tx, y + 4.5, { align: c.align }) })
        y += rowH + 1
      }
      drawHeader()

      self.results.forEach(function (b, idx) {
        if (y + rowH > ph - 22) { doc.addPage(); y = 12; drawHeader() }
        if (idx % 2 === 0) { doc.setFillColor(250, 251, 253); doc.rect(ml, y, cw, rowH, 'F') }
        doc.setDrawColor(...border); doc.setLineWidth(0.1); doc.line(ml, y + rowH, mr, y + rowH)
        doc.setFontSize(7); doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark)
        var fmtDate = function (d) { if (!d) return '—'; return new Date(d).toLocaleDateString('fr-FR') }
        var fmtTime = function (d) { if (!d) return ''; return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }
        var memo = (b.memo || '—'); if (memo.length > 40) memo = memo.substring(0, 38) + '..'
        var vals = ['#' + b.no, memo, self.getBatchTypeLabel(b), String(self.getProductsCount(b)), String(self.getTotalQuantity(b)), fmtDate(b.created_at), fmtTime(b.created_at)]
        cols.forEach(function (c, i) { var tx = c.align === 'right' ? c.x + c.w - 3 : (c.align === 'center' ? c.x + c.w / 2 : c.x + 3); doc.text(vals[i], tx, y + 4.2, { align: c.align }) })
        y += rowH
      })

      var totalPages = doc.internal.getNumberOfPages()
      for (var p = 1; p <= totalPages; p++) {
        doc.setPage(p); doc.setDrawColor(...border); doc.setLineWidth(0.2); doc.line(ml, ph - 10, mr, ph - 10)
        doc.setFontSize(6); doc.setFont('helvetica', 'bold'); doc.setTextColor(...primary); doc.text('KES LAKAY', ml, ph - 6)
        doc.setFont('helvetica', 'normal'); doc.setTextColor(...muted); doc.text('Page ' + p + ' / ' + totalPages, pw / 2, ph - 6, { align: 'center' })
        var now = new Date(); doc.text(now.toLocaleDateString('fr-FR') + ' ' + now.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }), mr, ph - 6, { align: 'right' })
      }
      doc.save('liste_lots.pdf')
    },

    exportExcel() {
      var self = this
      var XLSX = require('xlsx')
      var biz = {}; try { biz = JSON.parse(localStorage.business) || {} } catch (e) { biz = {} }
      var wsData = [['LISTE DES LOTS'], [biz.name || ''], []]
      wsData.push(['#', 'Mémo', 'Type', 'Produits', 'Quantité', 'Date', 'Heure'])
      self.results.forEach(function (b) {
        var fmtDate = function (d) { if (!d) return '—'; return new Date(d).toLocaleDateString('fr-FR') }
        var fmtTime = function (d) { if (!d) return ''; return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) }
        wsData.push([b.no, b.memo || '', self.getBatchTypeLabel(b), b.operations_count || 0, b.total_quantity || 0, fmtDate(b.created_at), fmtTime(b.created_at)])
      })
      var wb = XLSX.utils.book_new(); var ws = XLSX.utils.aoa_to_sheet(wsData)
      ws['!cols'] = [{ wch: 10 }, { wch: 40 }, { wch: 14 }, { wch: 10 }, { wch: 10 }, { wch: 12 }, { wch: 8 }]
      ws['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 3, c: 0 }, e: { r: 3, c: 6 } }) }
      ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }, { s: { r: 1, c: 0 }, e: { r: 1, c: 4 } }]
      XLSX.utils.book_append_sheet(wb, ws, 'Lots'); XLSX.writeFile(wb, 'liste_lots.xlsx')
    },
  },

  mounted() { this.load() },
}
</script>

<style scoped>
.search-input { width: 260px; padding: 8px 12px; font-size: 0.8125rem; border: 1px solid #cbd5e1; border-radius: 6px; outline: none; color: #1a2332; background: #fff; }
.search-input:focus { border-color: #194173; box-shadow: 0 0 0 2px rgba(25, 65, 115, 0.1); }
.search-input::placeholder { color: #94a3b8; }
.table-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; overflow: hidden; }
.table-card .v-simple-table thead th { font-size: 0.6875rem !important; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8 !important; background: transparent !important; border-bottom: 1px solid #e8ecf1 !important; padding: 8px 16px !important; }
.table-card .v-simple-table tbody td { font-size: 0.8125rem; padding: 10px 16px !important; border-bottom: 1px solid #f1f5f9 !important; }
.table-card .v-simple-table tbody tr:last-child td { border-bottom: none !important; }
.clickable-row { cursor: pointer; }
.clickable-row:hover { background: rgba(25, 65, 115, 0.03) !important; }
.empty-card { border: 1px solid #e8ecf1 !important; border-radius: 8px !important; box-shadow: 0 1px 3px rgba(0,0,0,0.04) !important; }
</style>
